import { plainTextToHtml } from '@pulsify/shared/html'
import { Bold, Italic, Link, List, Unlink } from 'lucide-react'
import {
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import * as m from '~/paraglide/messages'
import {
  normalizeLinkUrl,
  richTextToPlainText,
  sanitizePastedHtml,
  sanitizeRichText,
  toRichTextHtml,
} from '~/shared/lib/rich-text'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '~/shared/ui/popover'
import { RICH_TEXT_CONTENT_CLASSES } from '~/shared/ui/rich-text-content'

interface RichTextEditorProps {
  id?: string
  value: string
  onChange: (html: string) => void
  onBlur?: () => void
  placeholder?: string
  ariaLabel?: string
}

interface FormatState {
  bold: boolean
  italic: boolean
  list: boolean
  link: boolean
}

const NO_FORMAT: FormatState = { bold: false, italic: false, list: false, link: false }
const SINGLE_PARAGRAPH = /^<p>((?:(?!<\/?p>).)*)<\/p>$/s

function closestLink(node: Node | null, editor: HTMLElement): HTMLAnchorElement | null {
  const element = node instanceof Element ? node : (node?.parentElement ?? null)
  const anchor = element?.closest('a') ?? null
  return anchor && editor.contains(anchor) ? anchor : null
}

function selectionInside(editor: HTMLElement): Selection | null {
  const selection = document.getSelection()
  if (!selection?.rangeCount || !editor.contains(selection.anchorNode)) return null
  return selection
}

function isVisuallyEmpty(editor: HTMLElement): boolean {
  return !editor.textContent?.trim() && !editor.querySelector('li')
}

function selectRange(range: Range) {
  const selection = document.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
}

export function RichTextEditor({
  id,
  value,
  onChange,
  onBlur,
  placeholder,
  ariaLabel,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const emittedRef = useRef<string | null>(null)
  const savedRangeRef = useRef<Range | null>(null)
  const linkInputId = useId()
  const linkTextId = useId()
  const linkErrorId = useId()
  const [isEmpty, setIsEmpty] = useState(() => !richTextToPlainText(value))
  const [format, setFormat] = useState<FormatState>(NO_FORMAT)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkInput, setLinkInput] = useState('')
  const [linkText, setLinkText] = useState('')
  const [linkInvalid, setLinkInvalid] = useState(false)

  useEffect(() => {
    const editor = editorRef.current
    if (!editor || value === emittedRef.current) return
    editor.innerHTML = sanitizeRichText(toRichTextHtml(value))
    emittedRef.current = value
    setIsEmpty(isVisuallyEmpty(editor))
  }, [value])

  useEffect(() => {
    const refreshFormat = () => {
      const editor = editorRef.current
      const selection = editor ? selectionInside(editor) : null
      if (!editor || !selection) return
      setFormat({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        list: document.queryCommandState('insertUnorderedList'),
        link: closestLink(selection.anchorNode, editor) !== null,
      })
    }
    document.addEventListener('selectionchange', refreshFormat)
    return () => document.removeEventListener('selectionchange', refreshFormat)
  }, [])

  const emit = () => {
    const editor = editorRef.current
    if (!editor) return
    const html = sanitizeRichText(editor.innerHTML)
    const next = richTextToPlainText(html) ? html : ''
    setIsEmpty(isVisuallyEmpty(editor))
    if (next === emittedRef.current) return
    emittedRef.current = next
    onChange(next)
  }

  const run = (command: 'bold' | 'italic' | 'insertUnorderedList' | 'unlink') => {
    const editor = editorRef.current
    if (!editor) return
    editor.focus()
    if (command === 'unlink') {
      const selection = selectionInside(editor)
      const anchor = selection?.isCollapsed ? closestLink(selection.anchorNode, editor) : null
      if (anchor) selection?.selectAllChildren(anchor)
    }
    document.execCommand(command)
    emit()
  }

  const insertHtml = (html: string) => {
    if (html) document.execCommand('insertHTML', false, html)
    emit()
  }

  const openLink = () => {
    const editor = editorRef.current
    if (!editor) return
    const selection = selectionInside(editor)
    let range: Range
    if (selection) {
      range = selection.getRangeAt(0).cloneRange()
    } else {
      range = document.createRange()
      range.selectNodeContents(editor)
      range.collapse(false)
    }
    savedRangeRef.current = range
    const anchor = closestLink(range.startContainer, editor)
    setLinkInput(anchor?.getAttribute('href') ?? '')
    setLinkText(anchor?.textContent ?? range.toString())
    setLinkInvalid(false)
    setLinkOpen(true)
  }

  const applyLink = () => {
    const url = normalizeLinkUrl(linkInput)
    if (!url) {
      setLinkInvalid(true)
      return
    }
    const editor = editorRef.current
    const range = savedRangeRef.current
    savedRangeRef.current = null
    setLinkOpen(false)
    if (!editor || !range) return
    editor.focus()
    selectRange(range)
    const label = linkText.trim()
    const existing = closestLink(range.startContainer, editor)
    if (existing && (range.collapsed || existing.contains(range.endContainer))) {
      existing.setAttribute('href', url)
      if (label && label !== existing.textContent) existing.textContent = label
      emit()
    } else if (!range.collapsed && (!label || label === range.toString().trim())) {
      document.execCommand('createLink', false, url)
      emit()
    } else {
      const anchor = document.createElement('a')
      anchor.setAttribute('href', url)
      anchor.textContent = label || url.replace(/^mailto:/i, '')
      range.deleteContents()
      range.insertNode(anchor)
      range.setStartAfter(anchor)
      range.collapse(true)
      selectRange(range)
      emit()
    }
  }

  const handleFocus = () => {
    const editor = editorRef.current
    if (!editor) return
    document.execCommand('defaultParagraphSeparator', false, 'p')
    if (editor.innerHTML.trim() === '' || editor.innerHTML === '<br>') {
      editor.innerHTML = '<p><br></p>'
      const range = document.createRange()
      range.setStart(editor.firstChild as Node, 0)
      range.collapse(true)
      selectRange(range)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return
    const key = event.key.toLowerCase()
    if (key === 'b' || key === 'i' || key === 'k') event.preventDefault()
    if (key === 'b') run('bold')
    if (key === 'i') run('italic')
    if (key === 'k') openLink()
  }

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault()
    const html = event.clipboardData.getData('text/html')
    if (html) {
      insertHtml(sanitizePastedHtml(html))
      return
    }
    const converted = plainTextToHtml(event.clipboardData.getData('text/plain'))
    insertHtml(converted.replace(SINGLE_PARAGRAPH, '$1'))
  }

  const toolbarButton = (
    label: string,
    icon: ReactNode,
    onClick: () => void,
    pressed?: boolean,
  ) => (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className="aria-pressed:bg-accent aria-pressed:text-accent-foreground"
    >
      {icon}
    </Button>
  )

  return (
    <div className="bg-card dark:bg-input/30 border-input hover:border-brand/40 focus-within:border-brand focus-within:ring-brand/20 rounded-md border transition-[color,box-shadow] focus-within:ring-[3px]">
      <div
        role="toolbar"
        aria-label={m.richTextToolbar()}
        aria-controls={id}
        className="border-input flex items-center gap-1 border-b p-1"
      >
        {toolbarButton(m.richTextBold(), <Bold />, () => run('bold'), format.bold)}
        {toolbarButton(m.richTextItalic(), <Italic />, () => run('italic'), format.italic)}
        {toolbarButton(
          m.richTextBulletList(),
          <List />,
          () => run('insertUnorderedList'),
          format.list,
        )}
        <Popover open={linkOpen} onOpenChange={setLinkOpen}>
          <PopoverTrigger asChild>
            {toolbarButton(m.richTextLink(), <Link />, openLink, format.link)}
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="space-y-2"
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              const range = savedRangeRef.current
              savedRangeRef.current = null
              editorRef.current?.focus()
              if (range) selectRange(range)
            }}
          >
            <label htmlFor={linkTextId} className="text-sm font-medium">
              {m.richTextLinkText()}
            </label>
            <Input
              id={linkTextId}
              value={linkText}
              placeholder={m.richTextLinkTextPlaceholder()}
              onChange={(event) => setLinkText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return
                event.preventDefault()
                applyLink()
              }}
            />
            <label htmlFor={linkInputId} className="text-sm font-medium">
              {m.richTextLinkAddress()}
            </label>
            <Input
              id={linkInputId}
              value={linkInput}
              inputMode="url"
              placeholder={m.richTextLinkPlaceholder()}
              aria-invalid={linkInvalid}
              aria-describedby={linkInvalid ? linkErrorId : undefined}
              onChange={(event) => {
                setLinkInput(event.target.value)
                setLinkInvalid(false)
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return
                event.preventDefault()
                applyLink()
              }}
            />
            {linkInvalid && (
              <p id={linkErrorId} className="text-destructive text-sm">
                {m.richTextLinkInvalid()}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setLinkOpen(false)}>
                {m.cancel()}
              </Button>
              <Button type="button" size="sm" onClick={applyLink}>
                {m.richTextLinkApply()}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        {toolbarButton(m.richTextRemoveLink(), <Unlink />, () => run('unlink'))}
      </div>
      <div className="relative">
        {isEmpty && placeholder && (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-2 left-3 text-base md:text-sm"
          >
            {placeholder}
          </span>
        )}
        {/* biome-ignore lint/a11y/useSemanticElements: a multiline rich-text field has no native element */}
        <div
          ref={editorRef}
          id={id}
          role="textbox"
          tabIndex={0}
          aria-multiline="true"
          aria-label={ariaLabel}
          aria-placeholder={placeholder}
          contentEditable
          suppressContentEditableWarning
          className={cn(
            'min-h-32 px-3 py-2 text-base outline-none md:text-sm',
            RICH_TEXT_CONTENT_CLASSES,
          )}
          onInput={emit}
          onFocus={handleFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />
      </div>
    </div>
  )
}
